import { get, post } from '@/libs/http';
import { createApiMutationHook } from '@/hooks/use-api-mutation';
import { createApiQuery } from '@/hooks/use-api-query';

// 导入类型定义
import type { BindAgentReq, BindAgentRes } from '@/types/api/user-agent/bind-agent';
import type { WelfareRes } from '@/types/api/user-agent/welfare';
import type { MyRewardListReq, MyRewardListRes } from '@/types/api/user-agent/my-reward-list';
import type { MyRewardRes } from '@/types/api/user-agent/my-reward';
import type { UserDetailRes } from '@/types/api/user-agent/user-detail';
import type { WithdrawCommissionReq, WithdrawCommissionRes } from '@/types/api/user-agent/withdraw-commission';
import type { WithdrawCommissionListReq, WithdrawCommissionListRes } from '@/types/api/user-agent/withdraw-commission-list';
import type { InviteShareRes } from '@/types/api/user-agent/invite-share';
import type { FaqReq, FaqRes } from '@/types/api/user-agent/faq';
import { isReferralActivityOfflineAtom } from '@/store';
import { getDefaultStore } from 'jotai';
import type { StatusRes } from '@/types/api/user-agent/status';
import { AGENT_ENABLE_STATUS_ENUM, ERROR_CODE } from '@/features/referral/constants';
import type { GuestWelfareRes } from '@/types/api/user-agent/guest-welfare';
import type { MyRewardRuleRes } from '@/types/api/user-agent/my-reward-rule';

// ==================== API 函数定义 ====================

class HandleResponse<T = any>{
  responseData: T;
  constructor(responseData: T) {
    this.responseData = responseData
  }
  handleResponseCode = (response) => {
    const store = getDefaultStore();
    switch (response?.code) {
      case ERROR_CODE.OFF_LINE: {
        store.set(isReferralActivityOfflineAtom, true);
        return this;
      }
      default:
        break;
    }
    return this;
  }
  handleResponseData = (response) => {
    const store = getDefaultStore();
    if (response?.agent_enable !== undefined) {
      store.set(isReferralActivityOfflineAtom, response.agent_enable === AGENT_ENABLE_STATUS_ENUM.OFF);
    }
    return this;
  }
}

const handleResponse = <T = any>(response) => {
  const handle = new HandleResponse<T>(response);
  handle.handleResponseCode(response).handleResponseData(response);
  return handle.responseData;
}

/**
 * 代理活动状态
 * GET /user/agent/v1/status
 */
export const getAgentStatus = () => {
  return get<StatusRes>('/user/agent/v1/status').then((res) => {
    return handleResponse<StatusRes>(res as any)
  });
}

/**
 * 绑定代理
 * POST /user/agent/v1/bind/agent
 */
const bindAgent = ({ params }: { params: BindAgentReq }) => {
  return post<BindAgentRes>('/user/agent/v1/bind/agent', params).then((res) => {
    return handleResponse<BindAgentRes>(res as any)
  });
};

/**
 * 获取代理福利
 * GET /user/agent/v1/welfare
 */
// let time = 1;
const getWelfare = () => {
  // // Mock数据用于测试倒计时效果
  // const mockWelfareData: WelfareRes = {
  //   invite: {
  //     amount: 100,
  //     currency: 1,
  //     max_invite_count: 10,
  //     aleady_invite_count: 3
  //   },
  //   deposit: {
  //     amount: 2000,
  //     currency: 1
  //   },
  //   bet: {
  //     rate: time,
  //     time: time < 2 ? 10 : 15 // 25小时 (大于1天，显示天时分)
  //     // time: 3600, // 1小时 (小于1天，显示时分秒)
  //     // time: 60, // 1分钟 (小于1天，显示时分秒)
  //     // time: -1, // 无穷 (不显示)
  //   }
  // };
  // time++;
  // // 返回mock数据
  // return Promise.resolve(mockWelfareData);
  
  // 原始API调用（注释掉）
  return get<WelfareRes>('/user/agent/v1/welfare').then((res) => {
    return handleResponse<WelfareRes>(res as any)
  });
};

const getGuestWelfare = () => {
  return get<GuestWelfareRes>('/user/agent/v1/guest/welfare').then((res) => {
    return handleResponse<GuestWelfareRes>(res as any)
  });
};

/**
 * Mock 数据生成函数
 */
// const getMockRewardListByParams = (params: {
//   page: number;
//   page_size: number;
//   category: number;
//   date_type?: number;
// }): MyRewardListRes => {
//   const { page, page_size, category } = params;
  
//   // 生成 mock 用户数据
//   const generateUser = (index: number) => ({
//     avatar: `https://cggame123-bucket-dev.s3.dualstack.ap-southeast-1.amazonaws.com/user/public/image/avatar_120333212.avif`,
//     user_id: `user_${1000 + index}`,
//     username: `User${1000 + index}`,
//   });

//   // 生成 mock 列表项
//   const generateListItem = (index: number) => {
//     const now = Date.now();
//     const daysAgo = Math.floor(Math.random() * 7); // 0-6天前
//     const date = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
    
//     return {
//       date: date.toISOString().split('T')[0],
//       fulfill_amount: Math.floor(Math.random() * 10000) + 100,
//       fulfill_currency: Math.floor(Math.random() * 3) + 1, // 1-3
//       rebate_amount: Math.floor(Math.random() * 1000) + 10,
//       rebate_currency: Math.floor(Math.random() * 3) + 1, // 1-3
//       status: Math.random() > 0.5 ? 1 : 0, // 0: 待处理, 1: 已领取
//       user_info: generateUser(index),
//     };
//   };

//   // 根据 category 生成不同数量的总数据
//   const totalItems = category === 1 ? 156 : category === 2 ? 89 : 45;
  
//   // 计算当前页的数据
//   const startIndex = (page - 1) * page_size;
//   const endIndex = Math.min(startIndex + page_size, totalItems);
//   const currentPageItems = endIndex - startIndex;

//   // 生成当前页的列表数据
//   const list = Array.from({ length: currentPageItems }, (_, i) => 
//     generateListItem(startIndex + i)
//   );

//   // 计算总金额（所有页的总和）
//   const totalAmount = list.reduce((sum, item) => sum + item.rebate_amount, 0);

//   return {
//     agent_enable: 1,
//     amount: totalAmount,
//     currency: 1, // USDT
//     list,
//     total: totalItems,
//   };
// };

/**
 * 获取奖励明细
 * GET /user/agent/v1/my/reward/list
 */
const getMyRewardList = ({ params }: { params: MyRewardListReq }) => {
  // mock
  // return Promise.resolve(getMockRewardListByParams({
  //   page: parseInt(params.page || '1'),
  //   page_size: parseInt(params.page_size || '50'),
  //   category: parseInt(params.category),
  //   date_type: params.start_date ? 4 : undefined, // 如果有时间范围，认为是自定义时间
  // }));
  
  // 原始API调用
  return get<MyRewardListRes>('/user/agent/v1/my/reward/list', { params }).then((res) => {
    return handleResponse<MyRewardListRes>(res as any)
  });
};

/**
 * 获取规则
 * GET /user/agent/v1/reward/rules
 */
const getMyRewardRule = () => {
  // 原始API调用
  return get<MyRewardRuleRes>('/user/agent/v1/reward/rules', { }).then((res) => {
    return handleResponse<MyRewardRuleRes>(res as any)
  });
};

/**
 * 获取我的奖励
 * GET /user/agent/v1/my/reward
 */
const getMyReward = () => {
  return get<MyRewardRes>('/user/agent/v1/my/reward').then((res) => {
    return handleResponse<MyRewardRes>(res as any)
  });
};

/**
 * 获取用户代理信息详情
 * GET /user/agent/v1/user/detail
 */
const getUserDetail = () => {
  return get<UserDetailRes>('/user/agent/v1/user/detail').then((res) => {
    return handleResponse<UserDetailRes>(res as any)
  });
};

/**
 * 领取佣金
 * POST /user/agent/v1/withdraw/commission
 */
const withdrawCommission = ({ params }: { params: WithdrawCommissionReq }) => {
  return post<WithdrawCommissionRes>('/user/agent/v1/withdraw/commission', params).then((res) => {
    return handleResponse<{ code: number; }>(res as any)
  });
};

/**
 * 获取佣金领取明细
 * GET /user/agent/v1/withdraw/commission/list
 */
const getWithdrawCommissionList = ({ params }: { params: WithdrawCommissionListReq }) => {
  // Mock 数据
  // const mockData = [
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 200.75,
  //     currency: 2, // INR
  //     date: Math.floor(Date.now() / 1000) - 3600 * 1, // 1小时前
  //     status: 0 // 待处理
  //   },
  //   {
  //     amount: 150.25,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 2, // 2小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 300.00,
  //     currency: 3, // BRL
  //     date: Math.floor(Date.now() / 1000) - 3600 * 3, // 3小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 75.80,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 4, // 4小时前
  //     status: 0 // 待处理
  //   },
  //   {
  //     amount: 450.60,
  //     currency: 2, // INR
  //     date: Math.floor(Date.now() / 1000) - 3600 * 5, // 5小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 120.30,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 6, // 6小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 180.90,
  //     currency: 3, // BRL
  //     date: Math.floor(Date.now() / 1000) - 3600 * 7, // 7小时前
  //     status: 0 // 待处理
  //   },
  //   {
  //     amount: 250.40,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 8, // 8小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 95.70,
  //     currency: 2, // INR
  //     date: Math.floor(Date.now() / 1000) - 3600 * 9, // 9小时前
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  //   {
  //     amount: 100.50,
  //     currency: 1, // USDT
  //     date: Math.floor(Date.now() / 1000) - 3600 * 0, // 当前时间
  //     status: 1 // 已处理
  //   },
  // ];
  // console.log('====> 请求 params', params);
  // // 模拟分页逻辑
  // const page = params.page || 1;
  // const pageSize = params.page_size;
  // const startIndex = (page - 1) * pageSize;
  // const endIndex = startIndex + pageSize;
  // const paginatedData = mockData.slice(startIndex, endIndex);

  // // 模拟网络延时
  // return new Promise((resolve) => {
  //   setTimeout(() => {
  //     resolve({
  //       list: paginatedData,
  //       total: mockData.length
  //     });
  //   }, 1000); // 1秒延时
  // });

  // 原始 API 调用
  return get<WithdrawCommissionListRes>('/user/agent/v1/withdraw/commission/list', { params }).then((res) => {
    return handleResponse<WithdrawCommissionListRes>(res as any)
  });
};

/**
 * 获取邀请分享页面信息
 * GET /user/agent/v1/invite/share
 */
const getInviteShare = () => {
  return get<InviteShareRes>('/user/agent/v1/invite/share').then((res) => {
    return handleResponse<InviteShareRes>(res as any)
  });
};

/**
 * 获取代理FAQ
 * GET /user/agent/v1/faq
 */
const getFaq = ({ params }: { params: FaqReq }) => {
  return get<FaqRes>('/user/agent/v1/faq', { params }).then((res) => {
    return handleResponse<FaqRes>(res as any)
  });
};

// ==================== React Query Hooks ====================

/**
 * 获取代理活动状态 Hook (Query)
 */
export const useGetAgentStatus = createApiQuery(getAgentStatus, () => ['agent', 'status']);

/**
 * 绑定代理 Hook (Mutation)
 */
export const useBindAgent = createApiMutationHook(bindAgent);

/**
 * 获取代理福利 Hook (Query)
 */
export const useGetWelfare = createApiQuery(getWelfare, () => ['agent', 'welfare']);

/**
 * 获取代理福利 Hook (Query)
 */
export const useGetGuestWelfare = createApiQuery(getGuestWelfare, () => ['agent', 'guest-welfare']);

/**
 * 获取奖励明细 Hook (Query)
 */
export const useGetMyRewardList = createApiQuery(
  getMyRewardList,
  (params) => ['agent', 'reward-list', params.params.page, params.params.category, params.params.start_date, params.params.end_date]
);

/**
 * 获取我的奖励 Hook (Query)
 */
export const useGetMyReward = createApiQuery(getMyReward, () => ['agent', 'my-reward']);

/**
 * 获取用户代理信息详情 Hook (Query)
 */
export const useGetUserDetail = createApiQuery(getUserDetail, () => ['agent', 'user-detail']);

/**
 * 领取佣金 Hook (Mutation)
 */
export const useWithdrawCommission = createApiMutationHook(withdrawCommission);

/**
 * 获取佣金领取明细 Hook (Query)
 */
export const useGetWithdrawCommissionList = createApiQuery(
  getWithdrawCommissionList,
  (params) => ['agent', 'withdraw-commission-list', params.params.page, params.params.page_size]
);

/**
 * 获取邀请分享页面信息 Hook (Query)
 */
export const useGetInviteShare = createApiQuery(getInviteShare, () => ['agent', 'invite-share']);

/**
 * 获取代理FAQ Hook (Query)
 */
export const useGetFaq = createApiQuery(
  getFaq,
  (params) => ['agent', 'faq', params.params.page, params.params.page_size]
);

// ==================== 导出 API 函数 ====================

export {
  bindAgent,
  getWelfare,
  getGuestWelfare,
  getMyRewardList,
  getMyReward,
  getUserDetail,
  withdrawCommission,
  getWithdrawCommissionList,
  getInviteShare,
  getFaq,
  getMyRewardRule
};
